import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type {
  MorningBrief,
  MorningBriefDetail,
  MorningBriefIconState,
  MorningBriefPreference,
  MorningBriefSetting,
  MorningBriefAdminStatus,
  UpdateMorningBriefPreferenceInput,
  UpdateMorningBriefSettingInput,
} from '@atlas/contracts';

/**
 * Morning Brief data layer. Query keys are prefixed 'morning-brief' so the
 * AppShell SSE handler invalidates them on `morning_brief.generated` /
 * `notification.created`.
 */

const BASE = '/v2/morning-brief';

export type { MorningBrief, MorningBriefDetail, MorningBriefIconState, MorningBriefPreference };

/** Lightweight header-icon state — polled, cheap. */
export function useMorningBriefIcon() {
  return useQuery({
    queryKey: ['morning-brief', 'icon-state'],
    queryFn: () => api.get<MorningBriefIconState>(`${BASE}/icon-state`),
    refetchInterval: 60_000,
  });
}

export function useMorningBriefToday(enabled = true) {
  return useQuery({
    queryKey: ['morning-brief', 'today'],
    queryFn: () => api.get<{ brief: MorningBriefDetail | null }>(`${BASE}/today`),
    enabled,
  });
}

export function useMorningBriefById(id: string | undefined) {
  return useQuery({
    queryKey: ['morning-brief', 'detail', id],
    queryFn: () => api.get<{ brief: MorningBriefDetail }>(`${BASE}/${id}`),
    enabled: !!id,
  });
}

export function useMorningBriefHistory(page = 1, pageSize = 30) {
  return useQuery({
    queryKey: ['morning-brief', 'history', page, pageSize],
    queryFn: () =>
      api.get<{ items: MorningBrief[]; page: number; pageSize: number; total: number }>(
        `${BASE}/history?page=${page}&pageSize=${pageSize}`,
      ),
  });
}

export function useMorningBriefPreferences() {
  return useQuery({
    queryKey: ['morning-brief', 'preferences'],
    queryFn: () => api.get<{ preferences: MorningBriefPreference }>(`${BASE}/preferences`),
  });
}

/** Lifecycle + generation mutations; all invalidate the morning-brief family. */
export function useMorningBriefActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['morning-brief'] });

  // PATCH action endpoints take no payload; send `{}` so Fastify's empty-JSON
  // body guard doesn't reject them (same reason the notification client does).
  const markRead = useMutation({
    mutationFn: (id: string) => api.patch<{ brief: MorningBriefDetail }>(`${BASE}/${id}/read`, {}),
    onSuccess: invalidate,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.patch<{ brief: MorningBriefDetail }>(`${BASE}/${id}/dismiss`, {}),
    onSuccess: invalidate,
  });
  const markPopupShown = useMutation({
    mutationFn: (id: string) => api.patch<{ brief: MorningBriefDetail }>(`${BASE}/${id}/popup-shown`, {}),
    onSuccess: invalidate,
  });
  const generate = useMutation({
    mutationFn: () => api.post<{ brief: MorningBriefDetail }>(`${BASE}/generate`, {}),
    onSuccess: invalidate,
  });
  return { markRead, dismiss, markPopupShown, generate };
}

export function useUpdateMorningBriefPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMorningBriefPreferenceInput) =>
      api.put<{ preferences: MorningBriefPreference }>(`${BASE}/preferences`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['morning-brief'] }),
  });
}

// ---- Admin ----------------------------------------------------------------

export function useMorningBriefAdminStatus(enabled = true) {
  return useQuery({
    queryKey: ['morning-brief', 'admin', 'status'],
    queryFn: () => api.get<MorningBriefAdminStatus>('/v2/admin/morning-brief/status'),
    enabled,
    retry: false,
  });
}

export function useMorningBriefSettings(enabled = true) {
  return useQuery({
    queryKey: ['morning-brief', 'admin', 'settings'],
    queryFn: () => api.get<{ settings: MorningBriefSetting }>('/v2/admin/morning-brief/settings'),
    enabled,
    retry: false,
  });
}

export function useMorningBriefAdminActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['morning-brief'] });
  const updateSettings = useMutation({
    mutationFn: (input: UpdateMorningBriefSettingInput) =>
      api.put<{ settings: MorningBriefSetting }>('/v2/admin/morning-brief/settings', input),
    onSuccess: invalidate,
  });
  const generateAll = useMutation({
    mutationFn: () => api.post<{ generated: number; failed: number; skipped: number }>('/v2/admin/morning-brief/generate-all', {}),
    onSuccess: invalidate,
  });
  return { updateSettings, generateAll };
}
